import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { ChevronDown } from "lucide-react";
import { format, parse } from "date-fns";
import axios, { AxiosResponse } from "axios";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useBreedConstants } from "@/app/hooks/useBreedConstants";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useOrgServicesByFilterAPI } from "@/app/hooks/api_hooks/org/useOrgServicesByFilterAPI";
import { useOrgClinicProvidersAPI } from "@/app/hooks/api_hooks/org/useOrgClinicProvidersAPI";
import { useBookingSlotsListAPI } from "@/app/hooks/api_hooks/bookings/useBookingSlotsListAPI";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { SLOTS_APIS, CART_MODULE } from "@/api_framework/api_config";
import { IBookingCreationBody, IBookingCreationResponse } from "@/api_framework/api_modals/Bookings";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { buildBookingServiceMaps, toDiagnosticsReplacement } from "@/app/utils/bookingServiceSelection";
import WhatsappTemplatePreviewModal from "@/app/components/ui/WhatsappTemplatePreviewModal";
import { BookingFormData, FreeFollowUpMeta } from "./types";
import { CmFollowupTabs } from "./components/CmFollowupTabs";
import { CmBookingFilter } from "./components/CmBookingFilter";
import { CmSlotsGrid, ISlotDetail, IDoctorSlots } from "./components/CmSlotsGrid";
import { CmAppointmentConfirmation, CmAppointmentConfirmationData } from "./components/CmAppointmentConfirmation";
import { BOOKING_APIS } from "@/api_framework/api_config";
import { useSendBookingMirrorEventAPI } from "@/app/hooks/api_hooks/bookings/useSendBookingMirrorEventAPI";

interface FollowupBookingCreationProps {
  open: boolean;
  onClose: () => void;
  onCrossClose?: () => void;
  cmbookingRecommendations: any;
  petName?: string;
  petBreed?: string;
  userId: string;
  hideCannotCreateBooking?: boolean;
}

export const FollowupBookingCreation: React.FC<FollowupBookingCreationProps> = ({
  open,
  onClose,
  onCrossClose,
  cmbookingRecommendations,
  userId,
  hideCannotCreateBooking,
}) => {
  const { getRouteKey } = useRouter();
  const slotsRouteId = getRouteKey("SLOTS", "id");
  const { routeState: SRS } = useRouteInfo(slotsRouteId);
  const { providers } = SRS.state as ISlotsState;
  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const configuration = useConfiguration();
  const { execute: sendMirrorEvent } = useSendBookingMirrorEventAPI();
  const isCrossClosedRef = useRef(false);

  useEffect(() => {
    if (open) {
      sendMirrorEvent("FOLLOWUP_OPENED", {
        invoice_clinic_id: mappedClinicId,
        customer_id: userId,
        vms_user_id: userLogInfo || "",
      });
    }

    return () => {
      if (open && !isCrossClosedRef.current) {
        sendMirrorEvent("REMINDERS", {
          vms_user_id: userLogInfo,
          customer_id: userId
        });
        logFollowupEvent("FOLLOWUP_BOOKING_REMINDERS_SENT");
      }
    };
  }, [open, userId, userLogInfo, sendMirrorEvent]);

  const { execute: logFollowupAPI } = useClickLogAPI();
  const { petDetails, customerDetails, customerPets } = useRouteInfo(
    getRouteKey("CUSTOMER_PROFILE", "id"),
  ).routeState.state as ICustomerProfileState;
  const customerId = userId;
  const { pet_species } = useArrayConstants();
  const { dogBreeds, catBreeds } = useBreedConstants();

  const { VMSSourceId, bookingTypeNormal } = useStringConstants();
  const { serviceTypes, execute: fetchOrgServicesByFilter } = useOrgServicesByFilterAPI();
  const fetchProviders = useOrgClinicProvidersAPI("SLOTS");
  const fetchSlots = useBookingSlotsListAPI();
  const [isCreatingBooking, setIsCreatingBooking] = useState<boolean>(false);
  const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false);
  const [bookedByPet, setBookedByPet] = useState<Record<string, CmAppointmentConfirmationData>>({});
  const [isReasonModalOpen, setIsReasonModalOpen] = useState<boolean>(false);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const reasonDropdownRef = useRef<HTMLDivElement>(null);
  const [isRefreshingPayment, setIsRefreshingPayment] = useState<boolean>(false);
  const [isResendWhatsAppOpen, setIsResendWhatsAppOpen] = useState<boolean>(false);
  const [whatsAppBookingId, setWhatsAppBookingId] = useState<string>("");
  const [whatsAppPetName, setWhatsAppPetName] = useState<string>("");
  const [resendScript, setResendScript] = useState<string>("");
  const [resendInitialMobile, setResendInitialMobile] = useState<string>("");
  const [isResendScriptLoading, setIsResendScriptLoading] = useState<boolean>(false);
  const [isSendingResend, setIsSendingResend] = useState<boolean>(false);

  const handleRefreshPayment = async (bookingId: string) => {
    logFollowupEvent("FOLLOWUP_BOOKING_PAYMENT_REFRESH", {
      filter_values: { booking_id: bookingId },
    });
    setIsRefreshingPayment(true);
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const res = await axios.get(`${SLOTS_APIS?.IS_RESCHEDULE_PAID_ORNOT?.baseURL}`, {
        params: { booking_id: bookingId },
        headers: { Authorization: `${accessToken}` },
      });
      if (res.data?.status === true && res.data?.data) {
        const isPaid = res.data.data.is_paid === true || ["paid", "completed"].includes(String(res.data.data.payment_status).toLowerCase());
        if (isPaid) {
          toast.success("Payment received successfully!");
          setBookedByPet((prev) => {
            const updated = { ...prev };
            for (const petId in updated) {
              if (updated[petId].booking_id === bookingId) {
                updated[petId] = {
                  ...updated[petId],
                  payment_pending: false,
                };
              }
            }
            return updated;
          });
        } else {
          toast.error("Payment is still pending.");
        }
      }
    } catch (e: any) {
      toast.error("Failed to check payment status: " + e.message);
    } finally {
      setIsRefreshingPayment(false);
    }
  };

  const handleResendWhatsApp = async (bookingId: string, petName: string) => {
    logFollowupEvent("FOLLOWUP_BOOKING_PAYMENT_LINK_OPEN", {
      filter_values: { booking_id: bookingId, pet_name: petName },
    });
    setWhatsAppBookingId(bookingId);
    setWhatsAppPetName(petName);
    setResendScript("");
    setResendInitialMobile(customerDetails?.mobile ?? "");
    setIsResendWhatsAppOpen(true);
    setIsResendScriptLoading(true);
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const endpoint = BOOKING_APIS?.SEND_SCRIPT?.baseURL ?? "";
      const res = await axios.get(`${endpoint}?booking_id=${bookingId}`, {
        headers: { Authorization: `${accessToken}` },
      });
      if (res.data?.status) {
        setResendScript(res.data?.data?.whatsapp_text ?? "");
        setResendInitialMobile(res.data?.data?.phone_number ?? customerDetails?.mobile ?? "");
      }
    } catch {
      setResendScript("");
    } finally {
      setIsResendScriptLoading(false);
    }
  };

  const handleResendSend = async (mobile: string) => {
    if (isSendingResend) return;
    const mobileNumber = (mobile ?? "").replace(/\D/g, "");
    if (!mobileNumber) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (!whatsAppBookingId) {
      toast.error("Booking ID is missing.");
      return;
    }
    setIsSendingResend(true);
    logFollowupEvent("FOLLOWUP_BOOKING_PAYMENT_LINK_SEND", {
      phone: mobileNumber,
      filter_values: {
        booking_id: whatsAppBookingId,
        mobile: mobileNumber,
        pet_name: whatsAppPetName,
      },
    });
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const endpoint = SLOTS_APIS?.SEND_PAYMENT_LINK?.baseURL ?? "";
      const res = await axios.get(`${endpoint}${whatsAppBookingId}/?mobile=${mobileNumber}`, {
        headers: { Authorization: `${accessToken}` },
      });
      if (res.data?.status === true) {
        toast.success(res.data?.message || "WhatsApp sent successfully");
        setIsResendWhatsAppOpen(false);
      } else {
        toast.error(res.data?.message || "WhatsApp sending failed");
      }
    } catch {
      toast.error("WhatsApp sending failed");
    } finally {
      setIsSendingResend(false);
    }
  };

  const bookingServiceMaps = useMemo(() => buildBookingServiceMaps(serviceTypes), [serviceTypes]);

  // Tabs state — keyed by pet_id from cm_booking_recommendations
  const [activeTab, setActiveTab] = useState<string>(cmbookingRecommendations?.[0]?.pet_id ?? "");

  useEffect(() => {
    setSelectedReason("");
    setIsDropdownOpen(false);
  }, [activeTab]);

  const activeRecommendation = useMemo(
    () => (cmbookingRecommendations ?? []).find((rec: any) => rec.pet_id === activeTab) ?? null,
    [cmbookingRecommendations, activeTab],
  );

  const recommendationClinicId = activeRecommendation?.clinic_id || mappedClinicId || "";

  const activeFreeFollowUp: FreeFollowUpMeta | null = useMemo(() => {
    if (!activeRecommendation?.free_follow_up_eligible) return null;
    return {
      eligible: true,
      due_date: activeRecommendation.free_follow_up_due_date ?? null,
      strip_text: activeRecommendation.free_follow_up_strip ?? null,
    };
  }, [activeRecommendation]);

  const logFollowupEvent = (event_type: string, extras: Partial<IClickLogBody> = {}) => {
    // Build payload synchronously at click time so closure values are stable,
    // then defer the network call so the click log runs AFTER the main operation.
    const body: IClickLogBody = {
      event_type,
      scr_id: "FOLLOWUP_BOOKING_CREATION",
      pet_id: activeRecommendation?.pet_id ?? activeTab ?? undefined,
      clinic_id: recommendationClinicId || undefined,
      clinic_name: activeRecommendation?.clinic_name || mappedClinicName || undefined,
      source: activeRecommendation?.source ?? "EMR_FOLLOW_UP",
      ...extras,
      client_id: customerId || "",
      vms_user_id: userLogInfo || "",
      timestamp: Date.now(),
    };
    setTimeout(() => {
      logFollowupAPI(body);
    }, 0);
  };

  // Booking Summary form per pet — keyed by pet_id
  const [formsByPet, setFormsByPet] = useState<Record<string, BookingFormData>>({});
  const currentForm: BookingFormData = formsByPet[activeTab] ?? {
    date: activeRecommendation?.suggested_booking_date || format(new Date(), "yyyy-MM-dd"),
    serviceType: "",
    subService: activeRecommendation?.service_details?.sub_service_id ?? "",
    doctor: [],
    clinic: recommendationClinicId,
  };
  const setFormValue = (key: keyof BookingFormData, value: string | string[]) => {
    setFormsByPet((prev) => {
      const next = { ...currentForm, [key]: value };
      if (key === "serviceType") {
        next.subService = "";
      }
      return {
        ...prev,
        [activeTab]: next,
      };
    });
  };

  // Scrollable content ref + handler (mirrors handleScrollCart from MirrorScreenActions)
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const handleScrollCart = (direction: "up" | "down") => {
    const delta = direction === "up" ? -240 : 240;
    sendMirrorEvent("FOLLOWUP_SCROLL_STEP", {
      direction,
      delta,
      customer_id: customerId,
      vms_user_id: userLogInfo || "",
      pet_id: activeRecommendation?.pet_id ?? activeTab ?? null,
    });
    contentScrollRef.current?.scrollBy({ top: delta, behavior: "smooth" });
    logFollowupEvent("FOLLOWUP_BOOKING_SCROLL", {
      filter_values: { direction, delta },
    });
  };

  // Slots and selection states
  const [availableSlots, setAvailableSlots] = useState<IDoctorSlots[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);
  const [selectedSlotDetails, setSelectedSlotDetails] = useState<ISlotDetail | null>(null);

  // Clear selections on tab switch
  useEffect(() => {
    setSelectedSlotId(null);
    setSelectedSlotDetails(null);
  }, [activeTab]);

  // Load service types on mount / open
  useEffect(() => {
    if (open) {
      fetchOrgServicesByFilter({ filterType: "booking_vms" });
    }
  }, [open]);

  // Log modal open with default tab + resolved service/sub-service defaults.
  // Deferred until defaults are populated so we can include service_type_id/name + sub_service_id/name.
  const hasLoggedOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hasLoggedOpenRef.current = false;
      return;
    }
    if (hasLoggedOpenRef.current) return;
    const recs = cmbookingRecommendations ?? [];
    const defaultRec = recs.find((r: any) => r.pet_id === activeTab) ?? recs[0];
    if (!defaultRec) return;
    const defaultForm = formsByPet[defaultRec.pet_id];
    if (!defaultForm?.serviceType) return;
    const serviceTypeName =
      bookingServiceMaps.serviceTypeNamesByIdMap[defaultForm.serviceType] ?? "";
    const subServiceName =
      bookingServiceMaps.subServiceByIdMap[defaultForm.subService]?.sub_service_name ?? "";
    hasLoggedOpenRef.current = true;
    logFollowupEvent("FOLLOWUP_BOOKING_OPEN", {
      pet_id: defaultRec.pet_id,
      filter_values: {
        default_tab_pet_id: defaultRec.pet_id,
        default_tab_pet_name: defaultRec.pet_name,
        recommendations_count: recs.length,
        pet_ids: recs.map((r: any) => r.pet_id),
        service_type_id: defaultForm.serviceType,
        service_type_name: serviceTypeName,
        sub_service_id: defaultForm.subService,
        sub_service_name: subServiceName,
      },
    });
  }, [open, formsByPet, activeTab, bookingServiceMaps, cmbookingRecommendations]);

  // Prefill default service type from the active sub_service in the recommendation
  useEffect(() => {
    if (!open || serviceTypes.length === 0 || !activeTab) return;
    if (currentForm.serviceType) return;
    // A recommended legacy diagnostic appointment is booked as its Diagnostics replacement, when that is offered.
    const recommendedSubId = activeRecommendation?.service_details?.sub_service_id;
    const replacementSubId = recommendedSubId ? toDiagnosticsReplacement("", recommendedSubId).subServiceId : undefined;
    const subId =
      replacementSubId && bookingServiceMaps.subServiceToServiceIdMap[replacementSubId] ? replacementSubId : recommendedSubId;
    const matchedServiceId = subId ? bookingServiceMaps.subServiceToServiceIdMap[subId] : undefined;
    const matchedServiceTypeId = matchedServiceId
      ? bookingServiceMaps.serviceIdToServiceTypeIdMap[matchedServiceId]
      : undefined;
    const stId = matchedServiceTypeId ?? serviceTypes[0]?.id ?? "";
    const subs = bookingServiceMaps.subServicesByServiceTypeIdMap[stId] ?? [];
    setFormsByPet((prev) => ({
      ...prev,
      [activeTab]: {
        ...currentForm,
        serviceType: stId,
        subService: subId || subs[0]?.sub_service_id || "",
      },
    }));
  }, [open, serviceTypes, bookingServiceMaps, activeTab, activeRecommendation]);

  // Subservice auto-select when only one sub-service exists for the chosen service type
  useEffect(() => {
    if (!currentForm.serviceType) return;
    const availableSubs = bookingServiceMaps.subServicesByServiceTypeIdMap[currentForm.serviceType] ?? [];
    if (availableSubs.length === 1 && availableSubs[0].sub_service_id !== currentForm.subService) {
      setFormValue("subService", availableSubs[0].sub_service_id);
    }
  }, [currentForm.serviceType, bookingServiceMaps]);

  // Fetch Providers when date / service / subservice changes
  useEffect(() => {
    if (!open || !recommendationClinicId || !currentForm.date) return;
    const selectedServiceIds = currentForm.subService
      ? [bookingServiceMaps.subServiceToServiceIdMap[currentForm.subService]].filter(Boolean)
      : currentForm.serviceType
        ? (bookingServiceMaps.serviceTypeToServiceIdsMap[currentForm.serviceType] ?? [])
        : [];
    fetchProviders({
      ROTADate: currentForm.date,
      clinicIds: [recommendationClinicId],
      serviceId: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
      user_id: customerId ?? undefined,
    });
  }, [open, recommendationClinicId, currentForm.date, currentForm.serviceType, currentForm.subService, customerId]);

  // Fetch Available Slots
  useEffect(() => {
    if (!open || !recommendationClinicId || !currentForm.date || !currentForm.serviceType) {
      setAvailableSlots([]);
      setIsLoadingSlots(false);
      return;
    }
    setIsLoadingSlots(true);
    const selectedServiceId =
      (currentForm.subService && bookingServiceMaps.subServiceToServiceIdMap[currentForm.subService]) ||
      currentForm.serviceType;
    const petSpeciesId = activeRecommendation?.pet_species_type_id || petDetails?.pet_species_type_id || "";
    const payload: any = {
      user_id: customerId || "",
      service_type_id: currentForm.serviceType,
      service_id: selectedServiceId,
      pet_species_id: petSpeciesId,
      booking_date: currentForm.date,
      clinic_ids: [recommendationClinicId],
      staff_ids: currentForm.doctor ?? [],
      slot_status: "0",
      location_details: {
        latitude: null,
        longitude: null,
      },
    };
    if (currentForm.subService) {
      payload.sub_service_id = currentForm.subService;
    }
    sendMirrorEvent("FOLLOWUP_FETCH_SLOTS", {
      booking_date: currentForm.date,
      service_type_id: currentForm.serviceType,
      service_id: selectedServiceId,
      city_id: null,
      booking_id: null,
      clinic_ids: [recommendationClinicId],
      location_details: {
        latitude: null,
        longitude: null,
      },
      sub_service_id: currentForm.subService || undefined,
      staff_ids: currentForm.doctor ?? [],
      user_id: customerId || "",
      pet_species_id: petSpeciesId,
      slot_status: "0",
    });
    fetchSlots(payload).then((res) => {
      if (res && res.status && res.message) {
        const responseData = res.message as any[];
        const groupedDoctors: IDoctorSlots[] = [];
        responseData.forEach((clinic) => {
          (clinic.data ?? []).forEach((staff: any) => {
            if (
              currentForm.doctor?.length > 0 &&
              !currentForm.doctor.includes(staff.staff_id)
            ) {
              return;
            }
            const doctorSlots: ISlotDetail[] = [];
            (staff.data ?? []).forEach((slot: any) => {
              doctorSlots.push({
                slot_id: slot.slot_id,
                start_time: slot.start_time,
                end_time: slot.end_time,
                configuration_id: slot.configuration_id,
                prepaid_booking_slot: slot.prepaid_booking_slot,
                booking_fee: slot.booking_fee,
                staff_id: staff.staff_id,
                staff_name: staff.staff_name,
              });
            });
            if (doctorSlots.length > 0) {
              groupedDoctors.push({
                staff_id: staff.staff_id,
                staff_name: staff.staff_name,
                slots: doctorSlots,
              });
            }
          });
        });
        setAvailableSlots(groupedDoctors);
      } else {
        setAvailableSlots([]);
      }
    }).finally(() => {
      setIsLoadingSlots(false);
    });
  }, [
    open,
    recommendationClinicId,
    currentForm.date,
    currentForm.serviceType,
    currentForm.subService,
    currentForm.doctor,
    customerId,
    activeRecommendation,
    petDetails,
    sendMirrorEvent,
  ]);

  const handleSelectSlot = (slot: ISlotDetail) => {
    if (slot.slot_id === selectedSlotId) return;
    sendMirrorEvent("FOLLOWUP_SLOT_SELECTED", {
      slot_id: slot.slot_id,
    });
    const allSlots = availableSlots.flatMap((d) => d.slots);
    logFollowupEvent("FOLLOWUP_BOOKING_SLOT_SELECT", {
      number_of_slots: allSlots.length,
      prepaid_slots: allSlots.filter(
        (s) => s.prepaid_booking_slot?.toLowerCase() === "yes",
      ).length,
      staff_id: slot.staff_id,
      filter_values: {
        slot_id: slot.slot_id,
        start_time: slot.start_time,
        end_time: slot.end_time,
        staff_id: slot.staff_id,
        staff_name: slot.staff_name,
        booking_fee: slot.booking_fee,
        prepaid_booking_slot: slot.prepaid_booking_slot,
        configuration_id: slot.configuration_id,
        booking_date: currentForm.date,
      },
    });
    setSelectedSlotId(slot.slot_id);
    setSelectedSlotDetails(slot);
  };

  // WhatsApp invite preview state
  const [isInvitePreviewOpen, setIsInvitePreviewOpen] = useState<boolean>(false);
  const [inviteScript, _setInviteScript] = useState<string>("");
  const [isInviteScriptLoading, _setIsInviteScriptLoading] = useState<boolean>(false);
  const [inviteReasons, setInviteReasons] = useState<string[]>([]);

  const inviteReasonOptions = useMemo(
    () => inviteReasons.map((r, i) => ({ id: String(i + 1), title: r })),
    [inviteReasons],
  );

  const fetchInviteReasons = async () => {
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const endpoint = CART_MODULE?.CM_BOOKING_INVITE_REASONS?.baseURL ?? "";
      const res = await axios.get(endpoint, {
        headers: { Authorization: `${accessToken}` },
      });
      const reasons = res.data?.data?.send_invite_reasons;
      setInviteReasons(Array.isArray(reasons) ? reasons : []);
    } catch (e) {
      console.error("[invite-reasons] fetch failed:", e);
      setInviteReasons([]);
    }
  };

  useEffect(() => {
    if (open) fetchInviteReasons();
  }, [open]);

  // Clear booked state when modal closes so we don't leak across opens
  useEffect(() => {
    if (open) return;
    setBookedByPet({});
    setIsReasonModalOpen(false);
    setSelectedReason("");
    setIsDropdownOpen(false);
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reasonDropdownRef.current && !reasonDropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const buildBookingCreationBody = (): IBookingCreationBody | null => {
    if (!selectedSlotDetails) {
      toast.error("Please select a slot before creating a booking.");
      return null;
    }
    if (!recommendationClinicId) {
      toast.error("Clinic is missing for this recommendation.");
      return null;
    }
    if (!customerId || !activeRecommendation?.pet_id) {
      toast.error("Customer or pet details are missing.");
      return null;
    }
    if (!currentForm.serviceType || !currentForm.subService) {
      toast.error("Please select a service and sub-service.");
      return null;
    }
    const selectedServiceId =
      bookingServiceMaps.subServiceToServiceIdMap[currentForm.subService] || currentForm.serviceType;
    const serviceName = bookingServiceMaps.serviceTypeNamesByIdMap[currentForm.serviceType] ?? "";
    const subServiceName = bookingServiceMaps.subServiceByIdMap[currentForm.subService]?.sub_service_name ?? "";
    const serviceCartItems = currentForm.subService
      ? [{ type: "service", id: currentForm.subService, qty: 1 }]
      : [];
    const body: IBookingCreationBody = {
      source_id: "8",
      booking_type: bookingTypeNormal,
      slot_type: selectedSlotDetails.prepaid_booking_slot?.toLowerCase() === "yes" ? "paid" : "free",
      configuration_id: selectedSlotDetails.configuration_id ?? "",
      booking_date: currentForm.date,
      start_time: selectedSlotDetails.start_time,
      end_time: selectedSlotDetails.end_time,
      booking_start_date_time: `${currentForm.date} ${selectedSlotDetails.start_time}`,
      booking_end_date_time: `${currentForm.date} ${selectedSlotDetails.end_time}`,
      user_details: { id: customerId },
      pet_details: { id: activeRecommendation.pet_id },
      clinic_details: { id: recommendationClinicId },
      service_details: {
        service_type_id: currentForm.serviceType,
        service_id: selectedServiceId,
        service_name: serviceName,
        sub_service_id: currentForm.subService,
        sub_service_name: subServiceName,
      },
      staff_details: { id: selectedSlotDetails.staff_id },
      slot_details: { slot_id: selectedSlotDetails.slot_id },
      appointment_note: [],
      cart_details: {
        cart_items: serviceCartItems,
      },
    };
    return body;
  };

  const handleCreateBooking = async () => {
    if (isCreatingBooking) return;
    const body = buildBookingCreationBody();
    if (!body) return;
    const bookingPetId = activeTab;
    const petName = activeRecommendation?.pet_name ?? "pet";
    const createServiceTypeName =
      bookingServiceMaps.serviceTypeNamesByIdMap[currentForm.serviceType] ?? "";
    const createSubServiceName =
      bookingServiceMaps.subServiceByIdMap[currentForm.subService]?.sub_service_name ?? "";
    logFollowupEvent("FOLLOWUP_BOOKING_CREATE_CLICK", {
      staff_id: body.staff_details?.id,
      filter_values: {
        pet_id: activeRecommendation?.pet_id,
        pet_name: petName,
        booking_date: currentForm.date,
        service_type_id: currentForm.serviceType,
        service_type_name: createServiceTypeName,
        sub_service_id: currentForm.subService,
        sub_service_name: createSubServiceName,
        staff_ids: currentForm.doctor,
        selected_staff_id: selectedSlotDetails?.staff_id,
        selected_staff_name: selectedSlotDetails?.staff_name,
        clinic_id: recommendationClinicId,
        clinic_name: activeRecommendation?.clinic_name || mappedClinicName,
        slot_id: selectedSlotDetails?.slot_id,
        start_time: selectedSlotDetails?.start_time,
        end_time: selectedSlotDetails?.end_time,
        booking_fee: selectedSlotDetails?.booking_fee,
        prepaid_booking_slot: selectedSlotDetails?.prepaid_booking_slot,
        booking_body: body,
      },
    });
    setIsCreatingBooking(true);
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const endpoint = SLOTS_APIS?.BOOKING_CREATION?.baseURL ?? "";
      let bookingId = "";
      let succeeded = false;
      try {
        const res: AxiosResponse<IBookingCreationResponse> = await axios.post(endpoint, body, {
          headers: {
            Authorization: `${accessToken}`,
            "Content-Type": "application/json",
          },
        });
        succeeded = res.data?.status === true;
        bookingId = res.data?.data?.booking_id ?? "";
      } catch(error:any) {
        succeeded = false;
        toast.error(error?.response?.data?.message);
        return;
      }
      if (!succeeded) {
        return;
      }
      sendMirrorEvent("FOLLOWUP_BOOKING_CONFIRMED", {
        booking_id: bookingId,
        parent_name: customerDetails?.name || "",
        mobile: customerDetails?.mobile ? `+91 ${customerDetails.mobile}` : "",
      });
      toast.success(`Booking created successfully for ${petName}!`);
      const pet = (customerPets ?? []).find((p) => p.id === bookingPetId);
      const speciesId =
        activeRecommendation?.pet_species_type_id ||
        pet?.pet_species_type_id ||
        petDetails?.pet_species_type_id ||
        "";
      const breedId =
        activeRecommendation?.pet_breed_id ?? pet?.pet_breed_id ?? petDetails?.pet_breed_id;
      const speciesName =
        (pet_species ?? []).find((s: any) => String(s.id) === String(speciesId))?.title ?? "";
      const breedPool = String(speciesId) === "2" ? catBreeds ?? [] : dogBreeds ?? [];
      const breedName =
        breedId != null
          ? (breedPool as any[]).find((b: any) => Number(b.id) === Number(breedId))?.pet_breed ?? ""
          : "";
      const summary: CmAppointmentConfirmationData = {
        booking_id: bookingId,
        parent_name: customerDetails?.name ?? "",
        pet_name: activeRecommendation?.pet_name ?? pet?.name ?? "",
        pet_species_name: speciesName,
        pet_breed_name: breedName,
        date: currentForm.date,
        start_time: selectedSlotDetails?.start_time ?? "",
        end_time: selectedSlotDetails?.end_time ?? "",
        service_type_name: bookingServiceMaps.serviceTypeNamesByIdMap[currentForm.serviceType] ?? "",
        sub_service_name:
          bookingServiceMaps.subServiceByIdMap[currentForm.subService]?.sub_service_name ?? "",
        staff_name: selectedSlotDetails?.staff_name ?? "",
        clinic_name: activeRecommendation?.clinic_name || mappedClinicName || "-",
        payment_collected: selectedSlotDetails?.prepaid_booking_slot?.toLowerCase() === "yes",
        payment_pending: selectedSlotDetails?.prepaid_booking_slot?.toLowerCase() === "yes",
        booking_fee: Number(selectedSlotDetails?.booking_fee ?? 0),
      };
      setBookedByPet((prev) => ({ ...prev, [bookingPetId]: summary }));
      const apptServiceId =
        bookingServiceMaps.subServiceToServiceIdMap[currentForm.subService] ||
        currentForm.serviceType;
      const apptServiceTypeName =
        bookingServiceMaps.serviceTypeNamesByIdMap[currentForm.serviceType] ?? "";
      const apptSubServiceName =
        bookingServiceMaps.subServiceByIdMap[currentForm.subService]?.sub_service_name ?? "";
      logFollowupEvent("FOLLOWUP_BOOKING_APPOINTMENT_DETAILS", {
        pet_id: bookingPetId,
        staff_id: selectedSlotDetails?.staff_id,
        filter_values: {
          booking_id: bookingId,
          service_type_id: currentForm.serviceType,
          service_type_name: apptServiceTypeName,
          service_id: apptServiceId,
          sub_service_id: currentForm.subService,
          sub_service_name: apptSubServiceName,
          summary,
        },
      });
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const handleSubmitReason = () => {
    if (!selectedReason) return;
    const currentPetId = activeTab;
    const reason = selectedReason;
    logFollowupEvent("FOLLOWUP_BOOKING_CANNOT_CREATE", {
      pet_id: currentPetId,
      filter_values: {
        pet_id: currentPetId,
        pet_name: activeRecommendation?.pet_name,
        reason,
      },
    });
    setIsReasonModalOpen(false);
    setSelectedReason("");
    const recs = cmbookingRecommendations ?? [];
    const currentIndex = recs.findIndex((rec: any) => rec.pet_id === currentPetId);
    const nextRec = recs[currentIndex + 1];
    if (nextRec) {
      sendMirrorEvent("FOLLOWUP_PET_CHANGED", {
        pet_id: nextRec.pet_id,
      });
      setActiveTab(nextRec.pet_id);
    } else {
      onClose();
    }
  };

  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    const rec = (cmbookingRecommendations ?? []).find((r: any) => r.pet_id === tabId);
    logFollowupEvent("FOLLOWUP_BOOKING_TAB_CHANGE", {
      pet_id: tabId,
      filter_values: {
        pet_id: tabId,
        pet_name: rec?.pet_name,
        previous_pet_id: activeTab,
      },
    });
    sendMirrorEvent("FOLLOWUP_PET_CHANGED", {
      pet_id: tabId,
    });
    setActiveTab(tabId);
  };

  const isSameFormValue = (a: string | string[], b: string | string[]) => {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      const sa = [...a].sort();
      const sb = [...b].sort();
      return sa.every((v, i) => v === sb[i]);
    }
    return a === b;
  };

  const setFormValueWithLog = (key: keyof BookingFormData, value: string | string[]) => {
    const currentVal = currentForm[key] as string | string[];
    if (isSameFormValue(currentVal, value)) return;
    logFollowupEvent("FOLLOWUP_BOOKING_FILTER_CHANGE", {
      filter_values: {
        field: key,
        value,
        current_form: { ...currentForm, [key]: value },
      },
    });
    setFormValue(key, value);
  };

  const handleInviteMobileChange = (mobile: string) => {
    logFollowupEvent("FOLLOWUP_BOOKING_INVITE_MOBILE_CHANGE", {
      phone: mobile,
      filter_values: { mobile },
    });
  };

  const handleInviteCopyScript = () => {
    logFollowupEvent("FOLLOWUP_BOOKING_INVITE_COPY_SCRIPT", {
      filter_values: { script_length: inviteScript?.length ?? 0 },
    });
  };

  const handleInviteReasonChange = (reasonId: string) => {
    const reason = inviteReasonOptions.find((r) => r.id === reasonId);
    logFollowupEvent("FOLLOWUP_BOOKING_INVITE_REASON_SELECT", {
      filter_values: { reason_id: reasonId, reason: reason?.title },
    });
  };

  const handleResendMobileChange = (mobile: string) => {
    logFollowupEvent("FOLLOWUP_BOOKING_PAYMENT_LINK_MOBILE_CHANGE", {
      phone: mobile,
      filter_values: { mobile, booking_id: whatsAppBookingId },
    });
  };

  const handleResendCopyScript = () => {
    logFollowupEvent("FOLLOWUP_BOOKING_PAYMENT_LINK_COPY_SCRIPT", {
      filter_values: {
        booking_id: whatsAppBookingId,
        script_length: resendScript?.length ?? 0,
      },
    });
  };

  const handleInviteSend = async (mobile: string, reasonId?: string) => {
    if (isSendingInvite) return;
    const petName = activeRecommendation?.pet_name ?? "pet";
    const mobileNumber = (mobile ?? "").replace(/\D/g, "");
    if (!mobileNumber) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    if (!recommendationClinicId || !customerId || !activeRecommendation?.pet_id) {
      toast.error("Customer, pet, or clinic details are missing.");
      return;
    }
    if (!currentForm.serviceType || !currentForm.subService) {
      toast.error("Please select a service and sub-service before sending invite.");
      return;
    }
    const selectedServiceId =
      bookingServiceMaps.subServiceToServiceIdMap[currentForm.subService] || currentForm.serviceType;
    const subServiceName =
      bookingServiceMaps.subServiceByIdMap[currentForm.subService]?.sub_service_name ?? "";
    const payload = {
      mobile_number: mobileNumber,
      pet_name: activeRecommendation?.pet_name ?? "",
      service_name: subServiceName,
      booking_date: currentForm.date,
      clinic_id: recommendationClinicId,
      service_id: selectedServiceId,
      sub_service_id: currentForm.subService,
      service_type_id: currentForm.serviceType,
      customer_id: customerId,
      pet_id: activeRecommendation.pet_id,
      doctor_id: selectedSlotDetails?.staff_id ?? currentForm.doctor?.[0] ?? "",
      source: activeRecommendation?.source ?? "EMR_FOLLOW_UP",
      priority: activeRecommendation?.priority ?? "P1",
    };
    setIsSendingInvite(true);
    logFollowupEvent("FOLLOWUP_BOOKING_INVITE_SEND", {
      phone: mobileNumber,
      staff_id: payload.doctor_id,
      filter_values: {
        mobile: mobileNumber,
        reason_id: reasonId,
        payload,
      },
    });
    try {
      const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
      const endpoint = CART_MODULE?.CM_BOOKING_INVITE?.baseURL ?? "";
      const res = await axios.post(endpoint, payload, {
        headers: {
          Authorization: `${accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (res.data?.status === false) {
        toast.error(res.data?.message || `Failed to send invite for ${petName}.`);
        return;
      }
      toast.success(res.data?.message || `Booking invite sent for ${petName}!`);
      setIsInvitePreviewOpen(false);
    } catch {
      toast.error(`Failed to send invite for ${petName}.`);
    } finally {
      setIsSendingInvite(false);
    }
  };

  const getFormattedDate = (dateStr: string) => {
    try {
      const d = parse(dateStr, "yyyy-MM-dd", new Date());
      return format(d, "dd MMM yyyy, EEEE");
    } catch {
      return dateStr;
    }
  };

  const doctorsList = useSelectMenuReducer(providers ?? [], "name", "staff_id");
  const serviceTypesList = useSelectMenuReducer(serviceTypes, "name", "id");
  const subServices = bookingServiceMaps.subServicesByServiceTypeIdMap[currentForm.serviceType] ?? [];
  const subServicesList = useSelectMenuReducer(subServices, "sub_service_name", "sub_service_id");
  const displayClinicName = activeRecommendation?.clinic_name || mappedClinicName || "-";
  const activeBooked = bookedByPet[activeTab];
  const bookedRows = (cmbookingRecommendations ?? [])
    .map((rec: any) => bookedByPet[rec.pet_id])
    .filter(Boolean) as CmAppointmentConfirmationData[];
  const confirmationMobile = customerDetails?.mobile ? `+91 ${customerDetails.mobile}` : "";

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-hidden"
          onClose={() => { }}
        >
          <div className="flex items-center justify-center min-h-screen p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-black/50" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div
                className="inline-block bg-white rounded-[12px] text-left transform transition-all w-full max-w-[1150px] h-[700px] border border-slate-100 flex flex-col justify-between overflow-hidden font-sans relative z-50"
                style={{
                  boxShadow: "0px 20px 40px 0px rgba(0, 0, 0, 0.2)",
                }}
              >
                <CmFollowupTabs
                  activeTab={activeTab}
                  setActiveTab={handleTabChange}
                  onClose={() => {
                    logFollowupEvent("FOLLOWUP_BOOKING_CROSS_CLICK", {
                      filter_values: {
                        source: "cross_button",
                        booked_pet_ids: Object.keys(bookedByPet),
                      },
                    });
                    isCrossClosedRef.current = true;
                    onCrossClose?.();
                    onClose();
                  }}
                  cmbookingRecommendations={cmbookingRecommendations}
                  onScrollUp={() => handleScrollCart("up")}
                  onScrollDown={() => handleScrollCart("down")}
                  showCloseButton={hideCannotCreateBooking}
                />
                <div ref={contentScrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {activeBooked ? (
                    <CmAppointmentConfirmation
                      rows={bookedRows}
                      mobile={confirmationMobile}
                      activeBooking={activeBooked}
                      onRefreshPayment={handleRefreshPayment}
                      onResendWhatsApp={handleResendWhatsApp}
                      isRefreshingPayment={isRefreshingPayment}
                    />
                  ) : (
                    <>
                       {(activeRecommendation?.description || activeRecommendation?.free_follow_up_strip) && (
                        <div
                          className={
                            activeRecommendation?.free_follow_up_strip
                              ? "bg-[#E6F9F0] border border-[#34C471] rounded-[8px] px-3 py-2 text-[12px] font-medium text-[#1A6B3C] font-sans animate-glow-pulse"
                              : "bg-[#F0F6FF] border border-[#D6E4FF] rounded-[8px] px-3 py-2 text-[12px] font-medium text-[#193A6A] font-sans"
                          }
                        >
                          {activeRecommendation?.free_follow_up_strip
                            ? `${activeRecommendation.description ? activeRecommendation.description.trim() + " " : ""}${activeRecommendation.free_follow_up_strip.trim()}`
                            : activeRecommendation.description}
                        </div>
                      )}
                      <CmBookingFilter
                        petName={(activeRecommendation?.pet_name ?? "").toUpperCase()}
                        formData={currentForm}
                        setFormValue={setFormValueWithLog}
                        serviceTypesList={serviceTypesList}
                        subServicesList={subServicesList}
                        doctorsList={doctorsList}
                        clinicName={displayClinicName}
                        freeFollowUp={activeRecommendation?.free_follow_up_strip ? null : activeFreeFollowUp}
                      />
                      <CmSlotsGrid
                        formattedDate={getFormattedDate(currentForm.date)}
                        doctorsSlots={availableSlots}
                        selectedSlotId={selectedSlotId}
                        onSelectSlot={handleSelectSlot}
                        isLoading={isLoadingSlots}
                      />
                    </>
                  )}
                </div>
                <div className="bg-white px-[24px] pb-[24px] pt-0 h-[69px] flex items-end justify-end gap-3  shrink-0">
                  {activeBooked ? (
                    (() => {
                      const recs = cmbookingRecommendations ?? [];
                      const nextPendingRec = recs.find(
                        (rec: any) => !bookedByPet[rec.pet_id]
                      );
                      if (nextPendingRec) {
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              logFollowupEvent("FOLLOWUP_BOOKING_BOOK_ANOTHER_PET", {
                                filter_values: {
                                  next_pet_id: nextPendingRec.pet_id,
                                  next_pet_name: nextPendingRec.pet_name,
                                  previous_pet_id: activeTab,
                                  booked_pet_ids: Object.keys(bookedByPet),
                                },
                              });
                              sendMirrorEvent("FOLLOWUP_PET_CHANGED", {
                                pet_id: nextPendingRec.pet_id,
                              });
                              setActiveTab(nextPendingRec.pet_id);
                            }}
                            className="h-[36px] px-3 bg-[#1B68D3] hover:bg-[#155ab8] active:bg-[#114691] text-white rounded-[6px] text-[12px] font-semibold transition-all focus:outline-none flex items-center justify-center font-sans"
                          >
                            Book For Another Pet
                          </button>
                        );
                      }
                      return (
                        <button
                          type="button"
                          onClick={() => {
                            logFollowupEvent("FOLLOWUP_BOOKING_CLOSE_CLICK", {
                              filter_values: {
                                source: "footer_close_button",
                                booked_pet_ids: Object.keys(bookedByPet),
                              },
                            });
                            onClose();
                          }}
                          className="h-[36px] px-3 bg-[#1B68D3] hover:bg-[#155ab8] active:bg-[#114691] text-white rounded-[6px] text-[12px] font-semibold transition-all focus:outline-none flex items-center justify-center font-sans"
                        >
                          Close
                        </button>
                      );
                    })()
                  ) : (
                    <>
                      {!hideCannotCreateBooking && (
                        <button
                          type="button"
                          onClick={() => setIsReasonModalOpen(true)}
                          className="h-[36px] px-3 rounded-[6px] border border-[#1B68D3] text-[#1B68D3] font-semibold text-[12px] bg-white hover:bg-slate-50/50 transition-all focus:outline-none flex items-center justify-center font-sans"
                        >
                          Cannot Create Booking
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleCreateBooking}
                        disabled={isCreatingBooking || !selectedSlotId}
                        className={`h-[36px] px-3 rounded-[6px] text-white text-[12px] font-semibold transition-all focus:outline-none flex items-center justify-center ${isCreatingBooking || !selectedSlotId
                          ? "bg-[#1B68D380] cursor-not-allowed"
                          : "bg-[#1B68D3] hover:bg-[#155ab8] active:bg-[#114691]"
                          }`}
                      >
                        {isCreatingBooking ? "Creating..." : "Create Booking"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Transition.Child>
            <WhatsappTemplatePreviewModal
              open={isInvitePreviewOpen}
              onClose={() => setIsInvitePreviewOpen(false)}
              title="Send Booking Invite"
              initialMobile={customerDetails?.mobile ?? ""}
              script={isInviteScriptLoading ? "Loading…" : inviteScript}
              sendButtonLabel="Send Booking Invite"
              isSending={isSendingInvite}
              onSend={handleInviteSend}
              onCancel={() => setIsInvitePreviewOpen(false)}
              onMobileChange={handleInviteMobileChange}
              onCopyScript={handleInviteCopyScript}
              onReasonChange={handleInviteReasonChange}
              reasonOptions={inviteReasonOptions}
            />
            <WhatsappTemplatePreviewModal
              open={isResendWhatsAppOpen}
              onClose={() => setIsResendWhatsAppOpen(false)}
              title={`Resend WhatsApp${whatsAppPetName ? ` — ${whatsAppPetName}` : ""}`}
              initialMobile={resendInitialMobile}
              script={isResendScriptLoading ? "Loading…" : resendScript}
              sendButtonLabel="Send WhatsApp"
              isSending={isSendingResend}
              onSend={handleResendSend}
              onCancel={() => setIsResendWhatsAppOpen(false)}
              onMobileChange={handleResendMobileChange}
              onCopyScript={handleResendCopyScript}
            />
            <Transition.Root show={isReasonModalOpen} as={Fragment}>
              <Dialog
                as="div"
                className="z-[60] fixed inset-0 overflow-y-auto"
                onClose={() => {}}
              >
                <div className="flex justify-center items-center min-h-screen p-4 text-center">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Dialog.Overlay className="fixed inset-0 bg-black/50 transition-opacity" />
                  </Transition.Child>
                  <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Dialog.Panel className="inline-block bg-white rounded-[12px] text-left transform transition-all w-full max-w-[400px] p-6 shadow-xl relative z-50 font-sans">
                      <Dialog.Title as="h3" className="text-base font-semibold text-gray-900 mb-4">
                        Cannot Create Booking
                      </Dialog.Title>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                            Select Reason <span className="text-red-500">*</span>
                          </label>
                          <div className="relative" ref={reasonDropdownRef}>
                            <button
                              type="button"
                              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                              className="relative w-full cursor-pointer rounded-[6px] border border-gray-300 bg-white py-2.5 pl-4 pr-4 text-left text-[12px] font-semibold shadow-sm focus:outline-none focus:ring-1 focus:ring-[#1B68D3] focus:border-[#1B68D3] transition-all duration-150 flex items-center justify-between"
                            >
                              <span className="block truncate text-gray-900">
                                {selectedReason || "Select Reason"}
                              </span>
                              <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
                            </button>
                            {isDropdownOpen && (
                              <div className="absolute z-[70] mt-1 max-h-60 w-full overflow-auto rounded-[6px] bg-white py-1 text-xs shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none border border-gray-200">
                                {inviteReasons.map((reason, index) => {
                                  const isSelected = selectedReason === reason;
                                  return (
                                    <div
                                      key={index}
                                      onClick={() => {
                                        setSelectedReason(reason);
                                        setIsDropdownOpen(false);
                                      }}
                                      className={`cursor-pointer select-none py-2.5 px-3 text-left hover:bg-slate-50 transition-colors duration-150 ${isSelected
                                        ? "bg-slate-100 text-black font-semibold"
                                        : "text-gray-700 font-normal"
                                        }`}
                                    >
                                      {reason}
                                    </div>
                                  );
                                })}
                                {inviteReasons.length === 0 && (
                                  <div className="py-2.5 px-3 text-center text-gray-400">
                                    Loading reasons...
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                          <button
                            type="button"
                            disabled={!selectedReason}
                            onClick={handleSubmitReason}
                            className={`h-[36px] px-5 rounded-[6px] text-white text-[12px] font-semibold transition-all focus:outline-none ${!selectedReason
                              ? "bg-[#1B68D380] cursor-not-allowed"
                              : "bg-[#1B68D3] hover:bg-[#155ab8]"
                              }`}
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </Dialog>
            </Transition.Root>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default FollowupBookingCreation;
